// Sharing an airframe (decision 065): two steps that end with the invite
// link. Step one names the airframe and says who can join; step two shows
// the link to send to the friends, copied with one click. Under the host
// alone (the files are on this PC); the airframe's page gets Invite a
// pilot for the links made later.

import { formatBytes } from "@/host/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, unwrap } from "@/api";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { PublishInput, PublishOutcome, StatusRow } from "@/host/types";

/** Who can fly it, as the pilot chooses it: the platform's visibility and policy behind. "mine" (F443) puts the airframe on ShariFly for its owner alone: hidden, nobody let in, no link. */
export type JoinChoice = "mine" | "link" | "request" | "open";

export function joinChoiceInput(choice: JoinChoice): Pick<PublishInput, "visibility" | "join_policy"> {
  switch (choice) {
    case "mine":
      return { visibility: "hidden", join_policy: "invite" };
    case "link":
      return { visibility: "unlisted", join_policy: "invite" };
    case "request":
      return { visibility: "public", join_policy: "request" };
    case "open":
      return { visibility: "public", join_policy: "open" };
  }
}

/** The invite link of an airframe, as a friend opens it: the platform's address plus the code. */
export function inviteLink(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, "")}/invites/${code}`;
}

/** Mints a pilot link good for a week. */
export async function mintPilotLink(airframeId: string, origin: string): Promise<string> {
  const created = unwrap(await api.POST("/api/v1/airframes/{id}/invites", { params: { path: { id: airframeId } }, body: { role: "pilot", expires_in_hours: 168 } }));
  return inviteLink(origin, created.code);
}

/** Copies to the clipboard where the browser allows it; false otherwise, so the page says to select the link. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The platform's address the links point at: the shell's platform under the host, this site in a browser. */
export function usePlatformOrigin(): string {
  const host = useHost();
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  // The prerender of the public pages runs in Node (decision 136): no origin there, the links stay relative.
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  if (host) return info.data?.api_url || origin;
  return origin;
}

type ShareDialogProps = {
  row: StatusRow;
  /** Puts the airframe on the platform; resolves with what the platform made of it. */
  onShare: (input: PublishInput) => Promise<PublishOutcome>;
  onClose: () => void;
};

/** The dialog: step one the name and who can join, step two the link. */
/** The files with the sharing (decision 132): up, with the entry's figures, or why not. */
function FilesLine({ outcome }: { outcome: PublishOutcome }) {
  if (outcome.files) {
    return (
      <p className="text-xs text-ok" data-testid="share-files">
        Its files are up: entry {outcome.files.entry}, {outcome.files.files} files, {formatBytes(outcome.files.bytes)}. A member can book it and fly.
      </p>
    );
  }
  return (
    <p className="alert text-xs" data-testid="share-files">
      Its files are not up yet: {outcome.files_not_up ?? "nothing was uploaded"}
    </p>
  );
}

export function ShareDialog({ row, onShare, onClose }: ShareDialogProps) {
  const origin = usePlatformOrigin();
  const [name, setName] = useState("");
  const [choice, setChoice] = useState<JoinChoice>("link");
  const [copied, setCopied] = useState<boolean | null>(null);
  const share = useMutation({
    mutationFn: async () => {
      const outcome = await onShare({ name: name.trim() || null, description: null, ...joinChoiceInput(choice) });
      // Yours alone: no link to send.
      const link = choice === "mine" ? null : await mintPilotLink(outcome.id, origin);
      return { outcome, link };
    },
  });
  if (share.isSuccess && share.data.link === null) {
    const { outcome } = share.data;
    return (
      <section className="mt-3 flex flex-col gap-3 rounded-md border border-ok/40 bg-paper-2 p-4" aria-label={`${row.reg} is on ShariFly`} data-testid="share-done">
        <div className="flex flex-col gap-1">
          <span className="kicker">Done</span>
          <h3 className="text-lg font-semibold text-ok">{outcome.reg} is on ShariFly, yours alone</h3>
          <FilesLine outcome={outcome} />
          <p className="muted text-xs">Nobody else sees it. Book it from your hangar and fly: the hours, the logbook, the maintenance and where it stands are kept from now on. Let pilots in any time from the airframe's Members tab, or list it from its Settings page.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn btn-primary" onClick={onClose} data-testid="share-close">
            Done
          </button>
        </div>
      </section>
    );
  }
  if (share.isSuccess) {
    const { outcome, link } = share.data;
    return (
      <section className="mt-3 flex flex-col gap-3 rounded-md border border-ok/40 bg-paper-2 p-4" aria-label={`${row.reg} is shared`} data-testid="share-done">
        <div className="flex flex-col gap-1">
          <span className="kicker">Step 2 of 2</span>
          <h3 className="text-lg font-semibold text-ok">{outcome.reg} is shared</h3>
          <FilesLine outcome={outcome} />
          <p className="muted text-xs">Send this link to your friends. Whoever opens it becomes a pilot of {outcome.reg}; it works for 7 days. More links any time from the airframe's Members tab.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <code className="input flex-1 truncate" data-testid="share-link">
            {link!}
          </code>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void copyText(link!).then(setCopied);
            }}
            data-testid="copy-link"
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
        {copied === false ? <p className="muted text-xs">The clipboard is not reachable here: select the link and copy it.</p> : null}
        <ol className="muted list-decimal space-y-0.5 pl-5 text-xs">
          <li>Your friend opens the link, signs in with Discord, installs ShariFly.</li>
          <li>They press Book the aircraft on their Fleet and fly.</li>
          <li>You get a notification when they book it and when they return it.</li>
        </ol>
        <div className="flex gap-2">
          <button type="button" className="btn btn-primary" onClick={onClose} data-testid="share-close">
            Done
          </button>
        </div>
      </section>
    );
  }
  return (
    <form
      className="mt-3 flex flex-col gap-3 rounded-md border border-line bg-paper-2 p-4"
      aria-label={`Share ${row.reg} on ShariFly`}
      onSubmit={(e) => {
        e.preventDefault();
        share.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <span className="kicker">Step 1 of 2</span>
        <h3 className="text-lg font-semibold">Share {row.reg} on ShariFly</h3>
        <p className="muted text-xs">
          MSN {row.msn}, {row.livery}, {row.flights} flights in the log. The aircraft goes up with the sharing, X-Plane closed. You stay the owner and can change all of this on the airframe's Settings page: who flies it, whether it is listed.
        </p>
      </div>
      <label className="flex flex-col gap-1">
        <span className="kicker">Name shown to the pilots (optional)</span>
        <input className="input" aria-label="Airframe name" value={name} onChange={(e) => setName(e.target.value)} placeholder={`for example ${row.reg}, the club's Challenger`} maxLength={80} />
      </label>
      <fieldset className="flex flex-col gap-2 border-0 p-0">
        <legend className="kicker mb-1">Who can fly it</legend>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="join" value="mine" checked={choice === "mine"} onChange={() => setChoice("mine")} aria-label="Just me, for now" />
          <span>
            <strong>Just me, for now</strong> <span className="muted">· on ShariFly, nobody else sees it; the record of your flying starts</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="join" value="link" checked={choice === "link"} onChange={() => setChoice("link")} aria-label="Friends with the link" />
          <span>
            <strong>Friends with the link</strong> <span className="muted">· the link makes them a pilot; nothing to approve</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="join" value="request" checked={choice === "request"} onChange={() => setChoice("request")} aria-label="Anyone who asks, you approve" />
          <span>
            <strong>Anyone who asks, you approve</strong> <span className="muted">· listed in the Marketplace</span>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="join" value="open" checked={choice === "open"} onChange={() => setChoice("open")} aria-label="Anyone signed in" />
          <span>
            <strong>Anyone signed in</strong> <span className="muted">· listed, joins at once</span>
          </span>
        </label>
      </fieldset>
      {share.isError ? <p className="alert text-xs">{String(share.error)}</p> : null}
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={share.isPending} data-testid="share-submit">
          {choice === "mine" ? "Put it on ShariFly" : "Share"}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The way to share from the Fleet (decision 065): the airframes on this PC
 * that are not shared yet, one picked, then the dialog. Under the host
 * alone; nothing in a browser.
 */
export function ShareAnAirframe({ row, label }: { row?: StatusRow; label?: string } = {}) {
  const host = useHost();
  const queryClient = useQueryClient();
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const onShare = async (row: StatusRow, input: PublishInput) => {
    const outcome = await host!.publish(row.uuid, input);
    queryClient.invalidateQueries({ queryKey: ["host"] });
    queryClient.invalidateQueries({ queryKey: ["airframes"] });
    return outcome;
  };
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<StatusRow | null>(null);
  if (!host) return null;
  const candidates = (status.data ?? []).filter((r) => r.classification === "personal" && r.platform === null);
  // One airframe named (the hangar's row, decision 071): the dialog opens for it, no picking.
  if (row) {
    return (
      <div className="flex flex-col gap-2">
        {!picked ? (
          <button type="button" className="btn btn-sm btn-primary" onClick={() => setPicked(row)} data-testid={`share-this-${row.reg}`}>
            {label ?? "Share this airframe"}
          </button>
        ) : (
          <ShareDialog row={picked} onShare={(input) => onShare(picked, input)} onClose={() => setPicked(null)} />
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          setOpen((o) => !o);
          setPicked(null);
        }}
        disabled={candidates.length === 0}
        title={candidates.length === 0 ? "Every airframe on this PC is shared already, or none was found in the airframes folder" : undefined}
        data-testid="share-an-airframe"
      >
        Share an airframe
      </button>
      {open && !picked ? (
        <ul className="card flex flex-col gap-1 !p-2" aria-label="airframes on this PC not on ShariFly yet">
          {candidates.map((r) => (
            <li key={r.uuid}>
              <button type="button" className="btn w-full justify-start" onClick={() => setPicked(r)} data-testid={`share-pick-${r.reg}`}>
                {r.reg} · MSN {r.msn} · {r.livery} · {r.flights} flights
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {picked ? (
        <ShareDialog
          row={picked}
          onShare={(input) => onShare(picked, input)}
          onClose={() => {
            setOpen(false);
            setPicked(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Invite a pilot from the airframe's header (owners and captains): a pilot link, minted and copied in one click. */
export function InvitePilot({ airframeId, reg }: { airframeId: string; reg: string }) {
  const origin = usePlatformOrigin();
  const [copied, setCopied] = useState<boolean | null>(null);
  const mint = useMutation({
    mutationFn: () => mintPilotLink(airframeId, origin),
    onSuccess: (link) => {
      void copyText(link).then(setCopied);
    },
  });
  return (
    <div className="flex flex-col items-end gap-2">
      <button type="button" className="btn btn-primary" onClick={() => mint.mutate()} disabled={mint.isPending} data-testid="invite-pilot">
        Invite a pilot
      </button>
      {mint.isSuccess ? (
        <p className="alert alert-ok break-all text-xs" data-testid="invite-pilot-link">
          {copied ? "Invite link copied. " : "Invite link: "}
          <code>{mint.data}</code> Whoever opens it becomes a pilot of {reg}; it works for 7 days.
        </p>
      ) : null}
      {mint.isError ? <p className="alert text-xs">{String(mint.error)}</p> : null}
    </div>
  );
}
